/**
 * Dépôt de pièces jointes sur un point (POST /api/tickets/:id/attachments).
 *
 * Les pièces vont au dossier du point, comme le V1 : elles ne sont rattachées
 * à aucun message. Le serveur reste seul juge (interrupteur attach_upload,
 * point finalisé pour un client, types, taille) ; ce module ne fait que :
 *   - découper l'envoi par paquets de 10 fichiers (limite multipart de l'API) ;
 *   - écarter d'avance un fichier de plus de 25 Mo : l'API rejetterait tout
 *     le paquet (« Upload invalide ») au lieu de ce seul fichier ;
 *   - nommer une capture collée (Ctrl+V), que le navigateur appelle souvent
 *     « image.png » ;
 *   - dire si un glisser ou un collage porte des fichiers (sinon le
 *     comportement normal du champ de texte est laissé tel quel).
 */
import { authFetch } from './auth';

/** Taille maximale d'un fichier (UPLOAD_MAX_BYTES de l'API, 25 Mo). */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
/** Fichiers par requête (limite `files` du multipart de l'API). */
export const FILES_PER_REQUEST = 10;

export interface UploadError {
  filename: string;
  error: string;
}

export interface UploadOutcome {
  ok: number;
  errors: UploadError[];
}

/** Découpe une liste en paquets de `size` éléments au plus. */
export function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Sépare les fichiers trop gros (refusés d'avance) des autres. */
export function splitBySize<T extends { name: string; size: number }>(
  files: readonly T[],
  max = MAX_UPLOAD_BYTES,
): { accepted: T[]; rejected: UploadError[] } {
  const accepted: T[] = [];
  const rejected: UploadError[] = [];
  for (const f of files) {
    if (f.size > max) {
      rejected.push({ filename: f.name, error: `Fichier trop volumineux (max ${Math.round(max / 1024 / 1024)} Mo)` });
    } else {
      accepted.push(f);
    }
  }
  return { accepted, rejected };
}

/** Extension d'image d'après le type MIME d'une capture collée. */
function imageExt(mime: string): string {
  const sub = mime.split('/')[1]?.toLowerCase() ?? '';
  if (sub === 'jpeg') return 'jpg';
  return /^[a-z0-9]+$/.test(sub) ? sub : 'png';
}

/** Nom donné à une capture collée : « capture-AAAAMMJJ-HHMMSS.png » (heure locale). */
export function pastedImageName(mime: string, now: Date, index = 0): string {
  const p = (n: number) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}-${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}`;
  return `capture-${stamp}${index > 0 ? `-${index + 1}` : ''}.${imageExt(mime)}`;
}

/** Un glisser porte-t-il des fichiers (et non du texte ou un lien) ? */
export function dragHasFiles(types: readonly string[] | DOMStringList | null | undefined): boolean {
  if (!types) return false;
  return Array.from(types as ArrayLike<string>).includes('Files');
}

/**
 * Fichiers à déposer pour un collage, ou rien si le collage est du texte.
 * Un copier depuis Excel ou Word met à la fois du texte et une image dans le
 * presse-papiers : dans ce cas on laisse coller le texte. Seul un collage sans
 * texte (capture d'écran, image copiée) devient une pièce jointe.
 */
export function filesFromPaste(
  files: readonly File[],
  plainText: string,
  now: Date = new Date(),
): File[] {
  if (files.length === 0 || plainText.trim() !== '') return [];
  return files.map((f, i) => {
    const generic = !f.name || /^image\.(png|jpe?g|gif|webp|bmp)$/i.test(f.name);
    if (!f.type.startsWith('image/') || !generic) return f;
    return new File([f], pastedImageName(f.type, now, i), { type: f.type, lastModified: f.lastModified });
  });
}

interface ApiUploadBody {
  uploaded?: { filename: string; size_bytes: number }[];
  errors?: UploadError[];
  error?: string;
}

/** Envoie les fichiers au point, par paquets ; ne lève pas d'exception. */
export async function uploadAttachments(displayId: number, files: readonly File[]): Promise<UploadOutcome> {
  const { accepted, rejected } = splitBySize(files);
  const outcome: UploadOutcome = { ok: 0, errors: [...rejected] };
  for (const batch of chunk(accepted, FILES_PER_REQUEST)) {
    const form = new FormData();
    for (const f of batch) form.append('file', f, f.name);
    try {
      const res = await authFetch(`/api/tickets/${displayId}/attachments`, { method: 'POST', body: form });
      const body = (await res.json().catch(() => ({}))) as ApiUploadBody;
      outcome.ok += body.uploaded?.length ?? 0;
      if (body.errors && body.errors.length > 0) outcome.errors.push(...body.errors);
      else if (!res.ok) {
        // Refus global (point finalisé, dépôt fermé, requête invalide…).
        outcome.errors.push({ filename: '', error: body.error ?? `Envoi refusé (${res.status})` });
      }
    } catch (e) {
      outcome.errors.push({ filename: '', error: e instanceof Error ? e.message : 'Envoi impossible' });
    }
  }
  return outcome;
}
